import { goToNextPrevStepOrAnySlide } from '@goldcast/api/backend';

import { getEnv } from '@/constants';
import loggerService from '@/services/logger.service';
import { getAgoraRTC } from '@/services/agoraLoader';
import { CustomVideoTrackInitConfig, ILocalVideoTrack } from 'agora-rtc-sdk-ng';
import { EventState } from '../event/types';

/**
 * Updates a canvas element with a new slide image.
 *
 * @param canvasElement - The HTML canvas element to draw the slide on
 * @param imageUrl - URL of the slide image to display
 *
 * @remarks
 * - Clears canvas and draws new image maintaining aspect ratio
 * - Centers the image on canvas (portrait images will have black bars on sides)
 * - Handles CORS by setting crossOrigin to 'anonymous'
 * - Logs errors if image loading or drawing fails
 */
export async function updateSlidesCanvas(canvasElement: HTMLCanvasElement, imageUrl: string | null) {
  const ctx = canvasElement?.getContext('2d');
  if (!canvasElement || !ctx || !imageUrl) {
    return;
  }

  try {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.crossOrigin = 'anonymous';
      img.src = imageUrl;
    });

    // Clear canvas
    ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);

    drawImageOnCanvas(ctx, img, canvasElement);
  } catch (error) {
    loggerService.error('[Local Recording] Slides: Error updating canvas with new image:', error);
  }
}

function drawImageOnCanvas(ctx: CanvasRenderingContext2D, img: HTMLImageElement, canvasElement: HTMLCanvasElement) {
  // Calculate dimensions to maintain aspect ratio
  const canvasWidth = canvasElement.width;
  const canvasHeight = canvasElement.height;
  const canvasAspectRatio = canvasWidth / canvasHeight;
  const imageAspectRatio = img.naturalWidth / img.naturalHeight;

  let drawWidth: number;
  let drawHeight: number;
  let drawX: number;
  let drawY: number;

  if (imageAspectRatio > canvasAspectRatio) {
    // Image is wider than canvas (landscape) - fit to width
    drawWidth = canvasWidth;
    drawHeight = canvasWidth / imageAspectRatio;
    drawX = 0;
    drawY = (canvasHeight - drawHeight) / 2;
  } else {
    // Image is taller than canvas (portrait) - fit to height
    drawHeight = canvasHeight;
    drawWidth = canvasHeight * imageAspectRatio;
    drawX = (canvasWidth - drawWidth) / 2;
    drawY = 0;
  }

  // Draw image centered with proper aspect ratio
  ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
}

/**
 * Cleans up resources used for slide sharing by clearing intervals and canvas.
 *
 * @param rsSlidesCanvas - Object containing canvas elements and intervals to clean up
 * @param rsSlidesCanvas.canvasElement - The HTML canvas element to clear
 * @param rsSlidesCanvas.slidesCanvasInterval - Optional interval ID to clear
 * @param rsSlidesCanvas.animationFrameId - Optional animation frame ID to cancel (animated decks)
 */
export function stopSharingSlidesCanvas(rsSlidesCanvas: {
  canvasElement: HTMLCanvasElement;
  slidesCanvasInterval?: NodeJS.Timeout;
  animationFrameId?: number;
}) {
  clearInterval(rsSlidesCanvas.slidesCanvasInterval);

  if (rsSlidesCanvas.animationFrameId !== undefined) {
    cancelAnimationFrame(rsSlidesCanvas.animationFrameId);
  }

  // Clean up canvas element and cache to prevent memory leaks
  if (rsSlidesCanvas.canvasElement) {
    const canvas = rsSlidesCanvas.canvasElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }
}

/**
 * Creates or initializes a canvas element for displaying slides.
 *
 * @param slidesCanvas - Optional existing canvas configuration object
 * @param slidesCanvas.canvasElement - HTML canvas element for rendering slides
 * @returns The initialized canvas configuration object
 *
 * @remarks
 * - Sets canvas dimensions to 1920x1080
 * - Used for rendering slides that can be shared via screen sharing
 */
export function getSlidesCanvas(slidesCanvas?: { canvasElement: HTMLCanvasElement }) {
  if (!slidesCanvas) {
    slidesCanvas = { canvasElement: document.createElement('canvas') };
  }
  if (!slidesCanvas.canvasElement) {
    slidesCanvas.canvasElement = document.createElement('canvas');
  }

  slidesCanvas.canvasElement.width = 1920;
  slidesCanvas.canvasElement.height = 1080;

  return slidesCanvas;
}

export async function executeSlideNavigation(
  broadcast: { id: string; slide_state_version?: number | null },
  params: {
    action: 'next' | 'prev' | 'go_to_nth' | 'start' | 'stop';
    updatedSlide: number | null;
    updatedStep: number | null;
    currentPlayingSlideAsset: string | null;
  },
  onVersionConflict: (data: any) => void
) {
  try {
    return await goToNextPrevStepOrAnySlide({
      id: broadcast.id,
      body: {
        current_playing_slide_asset: params.currentPlayingSlideAsset,
        action: params.action,
        updated_slide_number: params.updatedSlide,
        slide_state_version: broadcast.slide_state_version ?? null,
        updated_step_number: params.updatedStep
      }
    });
  } catch (error: any) {
    if (error?.response?.data?.slide_state_version?.code === 'version_conflict') {
      onVersionConflict(error.response.data);
    }
    throw error;
  }
}

export function unshareSlidesOnLeave(broadcastId: string, userToken: string) {
  const url = `${getEnv('VUE_FETCHER_BACKEND_SERVER')}/event/broadcasts/${broadcastId}/update_slide/`;
  const payload = { current_playing_slide_number: 0, current_playing_slide_asset: null };

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`
    },
    body: JSON.stringify(payload),
    keepalive: true
  });
}

export async function getAgoraCustomTrack(event: EventState, track: MediaStreamTrack): Promise<ILocalVideoTrack> {
  const AgoraRTC = await getAgoraRTC();
  const optimizedParams: Partial<CustomVideoTrackInitConfig> =
    event?.useOptimizeRSCloudRecording && event.event.is_local_recording
      ? {
          width: 854,
          height: 480,
          optimizationMode: 'detail',
          bitrateMax: 800
        }
      : {};
  return AgoraRTC.createCustomVideoTrack({
    mediaStreamTrack: track,
    ...optimizedParams
  });
}
